import { useCallback } from 'react';
import { trackEvent } from '@app/libs/analytics';
import type { AnalyticsEvent } from '@app/libs/analytics';
import useAppStores from '@app/hooks/useAppStores';
import SupplierRegistrationMode from '@supplierRegistration/supplierRegistrationMode';

/**
 * Mode-scoped wrapper around `trackEvent` that injects the captured UTM
 * attribution params from the customer-data store into every event.
 *
 * `trackEvent` itself is a shared lib with no access to the mode store, so
 * this hook is the right layer to centralise the injection: call sites in the
 * vendor-portal flow use this instead of `trackEvent` directly, and a new
 * event added to the flow ships with attribution automatically — no per-call
 * `...store.utmParams` spread to forget.
 *
 * UTM params are read at call time (not at hook time), so an event fired after
 * the params are captured still carries them.
 */
export default function useVendorPortalTrackEvent(): (event: AnalyticsEvent) => void {
  const { supplierRegistrationCustomerDataStore } = useAppStores<SupplierRegistrationMode>();

  return useCallback(
    (event: AnalyticsEvent): void => {
      trackEvent({ ...event, ...supplierRegistrationCustomerDataStore.utmParams });
    },
    [supplierRegistrationCustomerDataStore],
  );
}
